import React, { useEffect, useRef } from 'react';
import { Redirect } from 'react-router-dom';
import { connect, ConnectedProps } from 'react-redux';
import { flowRight as compose } from 'lodash';
import { ELSCommonUIConstants, ELSPageLoader } from '../../components/els.components';
import Header from '../../components/header/Header.component';
import withHTMLHeadSEO from '../../hocs/with-html-head-seo/withHTMLHeadSEO.hoc';
import { locationActions } from '../../redux/location/location.actions';
import { RoutePath } from '../../components/app/app.constants';
import { studySelectors } from '../../redux/student-study/studentStudy.selectors';
import { FEATURE_FLAG } from '../../apis/eols-features-api/eols-features-api.constants';
import { isFeatureFlagEnabled } from '../../utilities/featureFlag.utilities';
import CreateQuizBottomBar from './CreateQuizBottomBar.component';
import CreateQuizBreadcrumb from './CreateQuizBreadcrumb.component';
import StepCard from './StepCard.component';
import {
  CREATE_QUIZ_STEPS,
  INITIAL_CREATE_QUIZ_SELECTION
} from './create-quiz.constants';
import { isStartQuizEnabled } from './create-quiz.utilities';

const mapStateToProps = state => ({
  messages: studySelectors.getMessages(state),
  featureFlagsGrouped: studySelectors.getFeatureFlagsGrouped(state),
  courseSectionId: studySelectors.getCourseSectionId(state),
});

const mapDispatchToProps = {
  redirect: locationActions.redirect,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export type CreateQuizPageProps = PropsFromRedux;

export const CreateQuizPageComponent = (props: CreateQuizPageProps) => {
  const {
    messages,
    featureFlagsGrouped,
    courseSectionId,
    redirect
  } = props;
  const goToSelfStudy = () => redirect(RoutePath.EAQ_STUDENT_STUDY);
  const stickyRef = useRef<HTMLDivElement>(null);
  const bottomBarRef = useRef<HTMLDivElement>(null);

  const isPageEnabled = Boolean(featureFlagsGrouped)
    && isFeatureFlagEnabled(FEATURE_FLAG.ENABLE_CREATE_QUIZ_PAGE, featureFlagsGrouped, courseSectionId);

  // Keeps keyboard focus from landing under the sticky header, breadcrumb and bottom bar
  useEffect(() => {
    const sticky = stickyRef.current;
    const bottomBar = bottomBarRef.current;
    if (!sticky || !bottomBar) {
      return undefined;
    }
    const root = document.documentElement;
    const stickyHeight = (element: HTMLElement) => (
      window.getComputedStyle(element).position === 'sticky' ? `${element.offsetHeight}px` : ''
    );
    const setScrollPadding = () => {
      root.style.scrollPaddingTop = stickyHeight(sticky);
      root.style.scrollPaddingBottom = stickyHeight(bottomBar);
    };
    setScrollPadding();
    window.addEventListener('resize', setScrollPadding);
    return () => {
      window.removeEventListener('resize', setScrollPadding);
      root.style.scrollPaddingTop = '';
      root.style.scrollPaddingBottom = '';
    };
  }, [isPageEnabled]);

  if (!featureFlagsGrouped) {
    return <ELSPageLoader />;
  }

  if (!isPageEnabled) {
    return <Redirect to={`/${ELSCommonUIConstants.security.States.PageNotFound}`} />;
  }

  return (
    <div className="c-ssa-create-quiz-page" data-testid="ssa-create-quiz-page">
      <div className="c-ssa-create-quiz-page__sticky" ref={stickyRef} data-testid="ssa-create-quiz-sticky">
        <header>
          <Header
            wordMark={(
              <>
                <span className="c-ssa-create-quiz-page__header-text-long" data-testid="ssa-create-quiz-header-text-long">
                  {messages.ADAPTIVE_QUIZZING_EAQ}
                </span>
                <span className="c-ssa-create-quiz-page__header-text-short" data-testid="ssa-create-quiz-header-text-short">
                  {messages.EAQ}
                </span>
              </>
            )}
          />
        </header>
        <CreateQuizBreadcrumb messages={messages} onSelfStudyClick={goToSelfStudy} />
      </div>
      <main className="c-ssa-create-quiz-page__main" data-testid="ssa-create-quiz-main">
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- the title is a deliberate tab stop between the breadcrumb and the steps */}
        <h1 className="c-ssa-create-quiz-page__title u-els-font-size-h3 u-els-bold" tabIndex={0} data-testid="ssa-create-quiz-title">
          {messages.CREATE_A_QUIZ}
        </h1>
        {CREATE_QUIZ_STEPS.map((step, index) => (
          <StepCard key={step.id} stepNumber={index + 1} title={step.title} messages={messages}>
            {step.render()}
          </StepCard>
        ))}
      </main>
      <CreateQuizBottomBar
        messages={messages}
        containerRef={bottomBarRef}
        isStartQuizEnabled={isStartQuizEnabled(INITIAL_CREATE_QUIZ_SELECTION, false)}
      />
    </div>
  );
};

const enhancers = [
  withHTMLHeadSEO({ title: messages => messages.CREATE_A_QUIZ }),
  connector
];

const CreateQuizPage = compose(...enhancers)(CreateQuizPageComponent) as React.ComponentType<any>;

export default CreateQuizPage;
